import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import apiClient from '../api/client';

const UploadContext = createContext(null);

export function useUpload() {
  const ctx = useContext(UploadContext);
  if (!ctx) throw new Error('useUpload must be used within UploadProvider');
  return ctx;
}

const POLL_INTERVAL_MS = 3000; // poll job status every 3 seconds
const STORAGE_KEY = 'spc_ocr_jobs'; // persist pending job IDs across navigation

/** Load persisted job IDs from localStorage */
function loadPersistedJobs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

/** Save active job IDs to localStorage */
function persistJobs(jobs) {
  try {
    const active = jobs.filter(j => j.status === 'pending' || j.status === 'processing' || j.status === 'queued');
    localStorage.setItem(STORAGE_KEY, JSON.stringify(active.map(j => ({
      id: j.id, jobId: j.jobId, name: j.name, size: j.size, status: j.status
    }))));
  } catch {}
}

/**
 * Global upload provider — queues files for background OCR processing.
 * Files are uploaded to blob instantly, OCR runs in the scheduler.
 * Jobs survive browser navigation and are polled every 3 seconds.
 */
export function UploadProvider({ children }) {
  const [jobs, setJobs] = useState(() => {
    // Restore any in-progress jobs from a previous session
    return loadPersistedJobs().map(j => ({ ...j, status: 'queued', result: null, error: null }));
  });
  const [isUploading, setIsUploading] = useState(false);
  const [lastBatchResults, setLastBatchResults] = useState(null);
  const [hasNewResults, setHasNewResults] = useState(false);

  const abortRef   = useRef(false);
  const pollingRef = useRef(null);

  // Persist jobs whenever they change
  useEffect(() => { persistJobs(jobs); }, [jobs]);

  // Clean up polling on unmount
  useEffect(() => {
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, []);

  /** Poll status of all queued/processing jobs */
  const pollJobs = useCallback(async () => {
    const activeJobs = jobs.filter(j => j.status === 'queued' || j.status === 'processing');
    if (activeJobs.length === 0) return;

    const jobIds = activeJobs.map(j => j.jobId).filter(Boolean);
    if (jobIds.length === 0) return;

    try {
      const res = await apiClient.get(`/invoice-upload/jobs?jobIds=${jobIds.join(',')}`);
      const statusMap = {};
      for (const s of res.data.jobs) statusMap[s.jobId] = s;

      let anyCompleted = false;
      let updatedJobs;
      setJobs(prev => {
        updatedJobs = prev.map(j => {
          const s = statusMap[j.jobId];
          // Job not in response = scheduler deleted it after successful processing
          if (!s) {
            anyCompleted = true;
            return { ...j, status: 'done' };
          }
          if (s.status === 'done') {
            anyCompleted = true;
            return { ...j, status: 'done', result: s, invoiceId: s.invoiceId };
          }
          if (s.status === 'failed') {
            anyCompleted = true;
            return { ...j, status: 'failed', error: s.error || 'OCR processing failed' };
          }
          if (s.status === 'processing') {
            return { ...j, status: 'processing' };
          }
          return j;
        });
        return updatedJobs;
      });

      if (anyCompleted) {
        setHasNewResults(true);
        // BUGFIX M-1: derive counts from updatedJobs (fresh state) not the stale closure jobs variable
        const doneCount   = (updatedJobs || []).filter(j => j.status === 'done').length;
        const failedCount = (updatedJobs || []).filter(j => j.status === 'failed').length;
        setLastBatchResults({ processed: doneCount, failed: failedCount });
        // Auto-clear done jobs from the popup after 4 seconds
        setTimeout(() => {
          setJobs(prev => prev.filter(j => j.status !== 'done'));
        }, 4000);
      }
    } catch (err) {
      console.warn('[UploadContext] Polling error:', err.message);
    }
  }, [jobs]);

  // Start/stop polling based on whether there are active jobs
  // BUGFIX H-3: placed AFTER pollJobs definition to avoid temporal dead zone (Cannot access before init)
  // Restarts interval whenever pollJobs changes so the callback always has a fresh jobs closure
  useEffect(() => {
    const hasActive = jobs.some(j => j.status === 'queued' || j.status === 'processing');
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    if (hasActive) {
      pollingRef.current = setInterval(pollJobs, POLL_INTERVAL_MS);
    }
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [jobs, pollJobs]);

  /**
   * Enqueue files: upload to blob immediately, create job rows, start polling.
   */
  const enqueueFiles = useCallback((files, options = {}) => {
    const documentType = options.documentType || 'Invoice';
    const newJobs = files.map(f => ({
      id: Math.random().toString(36).slice(2),
      jobId: null, // filled after POST /queue succeeds
      file: f,
      name: f.name,
      size: f.size,
      status: 'pending',
      result: null,
      error: null,
      documentType,
    }));

    setJobs(prev => [...prev, ...newJobs]);
    setLastBatchResults(null);
    setHasNewResults(false);

    return newJobs.map(j => j.id);
  }, []);

  /**
   * Start upload: send all pending files to /queue endpoint.
   * Returns immediately with job IDs — OCR happens in background.
   */
  const startUpload = useCallback(async () => {
    if (isUploading) return;
    setIsUploading(true);
    abortRef.current = false;

    // Get snapshot of pending jobs
    let pendingJobs = [];
    setJobs(prev => {
      pendingJobs = prev.filter(j => j.status === 'pending');
      return prev.map(j => j.status === 'pending' ? { ...j, status: 'uploading' } : j);
    });

    await new Promise(r => setTimeout(r, 50)); // let state settle

    if (pendingJobs.length === 0) { setIsUploading(false); return; }

    // Upload all files at once (blob upload is fast — only OCR is slow)
    try {
      const formData = new FormData();
      for (const job of pendingJobs) {
        formData.append('invoices', job.file);
      }
      if (pendingJobs[0].documentType !== 'Invoice') {
        formData.append('documentType', pendingJobs[0].documentType);
      }

      const response = await apiClient.post('/invoice-upload/queue', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const { jobs: queuedJobs = [], errors = [] } = response.data;

      // Map returned job IDs back to our local jobs by filename order
      setJobs(prev => {
        const updated = [...prev];
        let queueIdx = 0;
        for (let i = 0; i < updated.length; i++) {
          if (updated[i].status === 'uploading') {
            const qJob = queuedJobs[queueIdx];
            if (qJob) {
              updated[i] = { ...updated[i], status: 'queued', jobId: qJob.jobId };
              queueIdx++;
            } else {
              // This file failed to queue
              const errEntry = errors[queueIdx] || {};
              updated[i] = { ...updated[i], status: 'failed', error: errEntry.error || 'Failed to queue' };
              queueIdx++;
            }
          }
        }
        return updated;
      });

      // Kick off polling immediately
      setTimeout(pollJobs, 500);

    } catch (err) {
      // Network/server error — mark all uploading as failed
      const errMsg = err.response?.data?.error || err.message || 'Upload failed';
      setJobs(prev => prev.map(j =>
        j.status === 'uploading' ? { ...j, status: 'failed', error: errMsg } : j
      ));
    }

    setIsUploading(false);
  }, [isUploading, pollJobs]);

  /** Clear completed / failed jobs from the list */
  const clearCompleted = useCallback(() => {
    setJobs(prev => prev.filter(j => j.status === 'pending' || j.status === 'uploading' || j.status === 'queued' || j.status === 'processing'));
    setLastBatchResults(null);
    setHasNewResults(false);
  }, []);

  /** Clear everything */
  const clearAll = useCallback(() => {
    abortRef.current = true;
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
    setJobs([]);
    setIsUploading(false);
    setLastBatchResults(null);
    setHasNewResults(false);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  /** Dismiss the "new results" notification */
  const dismissResults = useCallback(() => {
    setHasNewResults(false);
  }, []);

  // Derived stats
  const pendingCount    = jobs.filter(j => j.status === 'pending').length;
  const uploadingCount  = jobs.filter(j => j.status === 'uploading' || j.status === 'queued' || j.status === 'processing').length;
  const doneCount       = jobs.filter(j => j.status === 'done').length;
  const failedCount     = jobs.filter(j => j.status === 'failed').length;
  const totalCount      = jobs.length;
  const activeCount     = pendingCount + uploadingCount;

  return (
    <UploadContext.Provider value={{
      jobs,
      isUploading,
      lastBatchResults,
      hasNewResults,
      enqueueFiles,
      startUpload,
      clearCompleted,
      clearAll,
      dismissResults,
      pendingCount,
      uploadingCount,
      doneCount,
      failedCount,
      totalCount,
      activeCount,
    }}>
      {children}
    </UploadContext.Provider>
  );
}
